import { parseMonths } from '../../../shared/period.utils';

/** Indica si hay meses específicos. */
export function hasSpecificMonths(months: string): boolean {
  return parseMonths(months).length > 0;
}

/** Indica si el conteo supera cero. */
export function isPositive(count: number): boolean {
  return count > 0;
}

/** Suma puntajes de subpreguntas. */
export function sumScores(subs: Array<{ score?: number }>): number {
  let total = 0;
  for (const sub of subs) total = total + scoreOf(sub);
  return total;
}

/** Normaliza puntaje unitario. */
export function scoreOf(sub: { score?: number }): number {
  return sub.score ?? 1;
}

/** Normaliza subitems a arreglo. */
export function asSubs(value: unknown): Array<{ score?: number }> {
  if (value) return value as Array<{ score?: number }>;
  return [];
}

/** Cuenta subitems. */
export function countSubitems(subs: unknown): number {
  return asSubs(subs).length;
}

/** Toma etiqueta de tipo. */
export function typeLabelOf(question: { questionType?: { label?: string } }): string {
  const label = question.questionType?.label;
  if (label) return label;
  return '—';
}

/** Indica si quiere subpreguntas. */
export function wantsSubitems(formData: { hasSubquestions?: boolean }): boolean {
  return formData.hasSubquestions === true;
}

/** Indica si pierde subitems. */
export function dropsSubitems(previous: any, wantsSubs: boolean): boolean {
  if (previous) return wantsSubs === false;
  return true;
}

/** Conserva subitems previos. */
export function keptSubitems(previous: any, wantsSubs: boolean): unknown[] {
  if (dropsSubitems(previous, wantsSubs) === true) return [];
  return previous?.subitems ?? [];
}

/** Mezcla pregunta con subitems. */
export function mergeQuestionRow(previous: any, data: Record<string, unknown>, wantsSubs: boolean): Record<string, unknown> {
  return { ...data, subitems: keptSubitems(previous, wantsSubs) };
}

/** Elige fila mezclada por id. */
export function pickMergedRow(row: { id?: string }, merged: { id?: string }): Record<string, unknown> {
  if (row.id === merged.id) return merged as Record<string, unknown>;
  return row as Record<string, unknown>;
}

/** Busca pregunta por id. */
export function findById(list: Array<{ id?: string }>, id: string): { id?: string } | undefined {
  return list.find((x) => rowMatches(x, id));
}

/** Compara id de fila. */
export function rowMatches(row: { id?: any }, id: string): boolean {
  return row.id === id;
}

/** Busca índice de subitem. */
export function findSubIndex(subs: Array<{ id?: string }>, id: string): number {
  return subs.findIndex((x) => rowMatches(x, id));
}

/** Indica si el índice existe. */
export function indexFound(idx: number): boolean {
  return idx > -1;
}

/** Copia lista de preguntas. */
export function copyQuestions(list: unknown[]): unknown[] {
  return [...list];
}

/** Sella datos de formulario. */
export function stampForm(formData: { categoryId?: unknown; year?: unknown; enabled?: unknown }, categoryId: string, year: string): void {
  formData.categoryId = categoryId;
  formData.year = year;
  formData.enabled = true;
}

/** Construye payload de pregunta. */
export function buildQuestionPayload(current: unknown[], data: { id?: string } & Record<string, unknown>, wantsSubs: boolean): unknown[] {
  const previous = findById(current as Array<{ id?: string }>, String(data.id));
  const merged = mergeQuestionRow(previous ?? null, data, wantsSubs);
  return payloadWith(current, data, merged);
}

/** Elige payload según alta o edición. */
export function payloadWith(current: unknown[], data: { id?: unknown }, merged: Record<string, unknown>): unknown[] {
  if (data.id) return remapQuestions(current, merged);
  return [...current, merged];
}

/** Reasigna fila mezclada. */
export function remapQuestions(current: unknown[], merged: Record<string, unknown>): unknown[] {
  return (current as Array<{ id?: string }>).map((q) => pickMergedRow(q, merged as { id?: string }));
}

/** Reemplaza subitem en pregunta. */
export function replaceSubitem(question: { subitems: Array<{ id?: string }> }, data: { id?: string }): void {
  const idx = findSubIndex(question.subitems, String(data.id));
  if (indexFound(idx) === true) setSubAt(question, data, idx);
  else pushSub(question, data);
}

/** Escribe subitem en posición. */
export function setSubAt(question: { subitems: unknown[] }, data: unknown, idx: number): void {
  question.subitems[idx] = data;
}

/** Agrega subitem. */
export function pushSub(question: { subitems: unknown[] }, data: unknown): void {
  question.subitems.push(data);
}

/** Indica si es bad request. */
export function isBadRequest(err: { status?: number }): boolean {
  return err.status === 400;
}

/** Indica si es fila padre. */
export function isParentRowFlag(row: { hasSubquestions?: boolean }, hasSubsFlag: boolean): boolean {
  return Boolean(row.hasSubquestions) || hasSubsFlag;
}

/** Indica si el sistema fuerza mensual. */
export function isForcedMonthlyFlag(system: { evaluateAllMonths?: boolean } | null): boolean {
  return Boolean(system?.evaluateAllMonths);
}

/** Etiqueta de conteo de meses. */
export function countLabel(months: string): string {
  return `${parseMonths(months).length} meses`;
}

/** Arma borrador de subpregunta. */
export function buildSubDraft(question: any, subquestion?: any): Record<string, unknown> {
  if (subquestion) return { ...subquestion, parentId: question.id };
  return { parentId: question.id };
}

/** Mueve elemento y devuelve copia. */
export function shiftedList(list: unknown[], i: number, delta: number, move: (arr: unknown[], from: number, to: number) => void): unknown[] {
  const arr = copyQuestions(list);
  move(arr, i, i + delta);
  return arr;
}
